import { useState, useEffect, useRef } from 'react';

/**
 * Hook für periodische Zeit-Updates (tick)
 * 
 * Features:
 * - Tick alle 30-60 Sekunden (konfigurierbar)
 * - Pausiert automatisch, wenn Tab/Fenster nicht sichtbar ist (Page Visibility API)
 * - Resumiert beim Fokus/Visibility-Change
 * - Memory-Leak Safe: Cleanup von Intervals und Event Listeners
 * 
 * @param intervalMs - Tick-Intervall in Millisekunden (Standard: 45s)
 * @returns Aktueller Timestamp (wird bei jedem Tick aktualisiert)
 */
export function useTimeTick(intervalMs: number = 45000): number {
  const [tick, setTick] = useState(() => Date.now());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(true);

  useEffect(() => {
    // Initial tick
    setTick(Date.now());

    // Page Visibility API: Pausiere, wenn Tab nicht sichtbar
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isVisibleRef.current = isVisible;

      if (isVisible) {
        // Tab wurde sichtbar: sofort ticken und Intervall starten
        setTick(Date.now());
        startInterval();
      } else {
        // Tab wurde verborgen: Intervall stoppen
        stopInterval();
      }
    };

    // Window Focus: Resumiere beim Fokus
    const handleFocus = () => {
      if (isVisibleRef.current) {
        setTick(Date.now());
        startInterval();
      }
    };

    const startInterval = () => {
      // Cleanup vorheriges Intervall
      stopInterval();
      
      intervalRef.current = setInterval(() => {
        setTick(Date.now());
      }, intervalMs);
    };

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Starte Intervall
    startInterval();

    // Event Listeners registrieren
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [intervalMs]);

  return tick;
}
